/* eslint-disable no-restricted-imports */
import { ProductType } from '@/data/types/Product';
import { findProductAttribute } from 'integration/utils/findProductAttribute';
import valueIsNullOrUndef from 'integration/utils/typeAssertions/valueIsNullOrUndef';

export const MAP_COLORS = {
	NEGRO: '#000000',
	GRIS: '#808080',
	PLATA: '#E3E4E5',
	BEIGE: '#EDE8E0',
	MORADO: '#572364',
	AZUL_MARINO: '#100D56',
	ROJO: '#960018',
	BLANCO: '#FFFFFF',
	AZUL_CLARO: '#90D5FF',
	ROSA: '#FFB5C0',
	VIOLETA: '#CFBEDB',
	CAFE: '#6F4E37',
	VERDE: '#23631F',
	NARANJA: '#D2691E',
	DORADO: '#C9A227',
	INDEFINIDO: '#BDBDBD',
	AMARILLO: '#FFDE21',
	KAKHI: '#D5C58A',
	MULTICOLOR: `url('/images/colors/Multicolor.jpg') center/cover`,
	FLORAL: `url('/images/colors/Floral.jpg') center/cover`,
	FUCSIA: '#C3005A',
	TINTO: '#550000',
	CORAL: '#FF7F69',
	PRINTED: `url('/images/colors/Printed.jpg') center/cover`,
} as const;

export type ColorMap = typeof MAP_COLORS;
export type ColorKey = keyof ColorMap;

export type ProductColorResult = { color1: string; color2?: string };

const generateKey = (color: string): string =>
	color
		.normalize('NFD')
		.replaceAll(/[\u0300-\u036f]/g, '')
		.toUpperCase()
		.trim()
		.replaceAll(/\s+/g, '_');

export const getColorFromMap = (colorName: string): string => {
	const key = generateKey(colorName);
	return MAP_COLORS[key as ColorKey] || MAP_COLORS.INDEFINIDO;
};

export const getProductColor = (product: ProductType | string): ProductColorResult => {
	if (typeof product === 'string') {
		return { color1: getColorFromMap(product), color2: undefined };
	}

	const color1Value = findProductAttribute(product.attributes, 'Color1')?.value || 'INDEFINIDO';
	const color2Value = findProductAttribute(product.attributes, 'Color2')?.value;

	return {
		color1: getColorFromMap(color1Value),
		color2: color2Value ? getColorFromMap(color2Value) : undefined,
	};
};

export const hasTwoColors = (
	colors: ProductColorResult
): colors is { color1: string; color2: string } => !valueIsNullOrUndef(colors.color2);

export const getProductColorName = (product: ProductType | string): string => {
	if (typeof product === 'string') {
		return product;
	}

	const color1Value = findProductAttribute(product.attributes, 'Color1')?.value;
	const color2Value = findProductAttribute(product.attributes, 'Color2')?.value;

	return [color1Value, color2Value].filter(Boolean).join(' / ');
};

export const orderColorVariantsByBase = (
	variants: ProductType[],
	basePartNumber: string
): ProductType[] => {
	const baseIndex = variants.findIndex((variant) => variant.partNumber === basePartNumber);

	if (baseIndex <= 0) {
		return variants;
	}

	const ordered = [...variants];
	const [base] = ordered.splice(baseIndex, 1);
	ordered.unshift(base);

	return ordered;
};
